import { useMemo, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useGmud } from '../../../api/queries/gmuds'
import { AvisoDeErro, Carregando, EstadoVazio } from '../../../components/Estados'
import { dataHora } from '../../../lib/datas'
import { ControlesDoRelatorio } from './ControlesDoRelatorio'
import { DocumentoRdm } from './DocumentoRdm'
import './relatorio.css'
import { TEXTOS } from '../../../lib/glossario'

/**
 * O RDM de uma solicitação, pronto para imprimir ou salvar em PDF.
 *
 * Vive FORA do `Layout` (ver `routes/router.tsx`): menu lateral, barra superior e rodapé da
 * aplicação não podem sair no papel. Continua protegida por sessão — o documento traz o
 * plano inteiro da mudança.
 *
 * Não há biblioteca de PDF aqui, e não é economia: o PDF quem gera é o navegador, pelo botão
 * "Imprimir / Salvar em PDF". No sistema anterior o dompdf montava o arquivo no servidor, o
 * que custava uma dependência, um endpoint e uma fonte embutida para produzir o mesmo papel
 * que a janela de impressão produz. O que se perde é o nome do arquivo já pronto; o que se
 * ganha é a pré-visualização fiel na tela e um documento que se lê com leitor de tela antes
 * de virar imagem.
 *
 * @returns a página do relatório
 */
export function PaginaRelatorioRdm() {
  const { id } = useParams()
  const gmudId = Number(id)
  const enderecoValido = Number.isFinite(gmudId)
  const { data: gmud, isPending, isError, error } = useGmud(gmudId)

  // Fixado na montagem, e não calculado a cada renderização: a "Data da Emissão" sai nas
  // quatro folhas, e um `new Date()` no meio do JSX faria as quatro discordarem entre si
  // quando o React reconciliasse a árvore durante a impressão.
  const emitidoEm = useMemo(() => dataHora(new Date().toISOString()), [])

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <ControlesDoRelatorio voltarPara={enderecoValido ? `/gmuds/${gmudId}` : '/gmuds'} pronto={Boolean(gmud)} />

      <main className="px-4 py-6 print:p-0">
        {!enderecoValido && (
          <Aviso>
            <EstadoVazio
              titulo={TEXTOS.enderecoInvalido}
              ajuda="Este link não aponta para uma solicitação. Volte para a lista e abra o registro pelo código."
            />
          </Aviso>
        )}
        {enderecoValido && isPending && <Carregando texto="Montando o relatório…" />}
        {enderecoValido && isError && (
          <Aviso>
            <AvisoDeErro erro={error} titulo="Não foi possível montar o relatório" />
          </Aviso>
        )}
        {gmud && <DocumentoRdm gmud={gmud} emitidoEm={emitidoEm} />}
      </main>
    </div>
  )
}

/**
 * A moldura dos estados que não são o documento.
 *
 * Eles não usam o `relatorio.css` de propósito: erro e endereço inválido são a aplicação
 * falando com o usuário, não o formulário oficial, e não faria sentido servirem numa folha
 * A4 com faixa laranja.
 *
 * @param props o aviso a mostrar
 * @returns o aviso centralizado na largura de leitura
 */
function Aviso({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-2xl">{children}</div>
}
